/**
 * /shared/:token — the gate a share link lands on.
 *
 * The link identifies a share; it does not grant access. This page asks the API
 * to mail a code to the address recorded on the share (never one typed here),
 * then exchanges that code for a guest session. Someone holding a forwarded
 * link reaches exactly this screen and the code goes to the original recipient.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, KeyRound, Loader2, MailCheck, ShieldCheck } from "lucide-react";

import GuestShell from "@/components/GuestShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { useGuest } from "@/contexts/GuestContext";
import { ApiError } from "@/lib/api";
import { guestApi } from "@/lib/guestApi";

interface CodeRequestResult {
  email_masked: string;
  requires_sign_in: boolean;
}

interface SessionResult {
  guest_token: string;
  email: string;
  recipient_name: string | null;
}

const RESEND_SECONDS = 60;

export default function GuestAccessPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { startSession } = useGuest();

  const [stage, setStage] = useState<"requesting" | "code" | "blocked" | "sign_in">("requesting");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [blockedMessage, setBlockedMessage] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_SECONDS);
  // StrictMode double-invokes effects in dev; without this the first mount
  // sends two code requests and the second trips the resend cooldown.
  const requested = useRef(false);

  const requestCode = useCallback(
    async (silent = false) => {
      if (!token) return;
      try {
        const res = await guestApi.postPublic<CodeRequestResult>("/guest/request-code", {
          share_token: token,
        });
        setMaskedEmail(res.email_masked);
        if (res.requires_sign_in) {
          setStage("sign_in");
          return;
        }
        setStage("code");
        setResendIn(RESEND_SECONDS);
        if (!silent) toast.success("A new code is on its way");
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Something went wrong.";
        // 429 means a code was just sent — that's a usable state, not a dead end.
        if (err instanceof ApiError && err.status === 429) {
          setStage("code");
          setResendIn(RESEND_SECONDS);
          if (!silent) toast.error(message);
          return;
        }
        setBlockedMessage(message);
        setStage("blocked");
      }
    },
    [token, toast],
  );

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    void requestCode(true);
  }, [requestCode]);

  useEffect(() => {
    if (stage !== "code" || resendIn <= 0) return;
    const id = window.setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => window.clearInterval(id);
  }, [stage, resendIn]);

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    if (!token || code.trim().length < 4) return;
    setVerifying(true);
    try {
      const res = await guestApi.postPublic<SessionResult>("/guest/verify-code", {
        share_token: token,
        code: code.trim(),
      });
      startSession(res.guest_token, res.email, res.recipient_name);
      navigate("/guest/listings", { replace: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Verification failed.");
      setCode("");
    } finally {
      setVerifying(false);
    }
  }

  return (
    <GuestShell showAccountBar={false}>
      <div className="mx-auto flex w-full max-w-md flex-col justify-center px-4 py-14 sm:py-20">
        {stage === "requesting" && (
          <div className="rounded-2xl border border-border/80 bg-card p-8 text-center shadow-card">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <p className="mt-4 text-sm text-muted-foreground">Checking your link&hellip;</p>
          </div>
        )}

        {stage === "blocked" && (
          <div className="rounded-2xl border border-border/80 bg-card p-8 text-center shadow-card">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <h1 className="mt-5 text-xl font-bold text-foreground">This link isn&rsquo;t available</h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{blockedMessage}</p>
            <Button asChild className="mt-6 w-full">
              <Link to="/register">
                Explore BusinessLocating
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}

        {stage === "sign_in" && (
          <div className="rounded-2xl border border-border/80 bg-card p-8 text-center shadow-card">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-primary/12 text-primary">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <h1 className="mt-5 text-xl font-bold text-foreground">You already have an account</h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              This address is registered with BusinessLocating. Sign in to view this listing with your
              own account.
            </p>
            <Button asChild className="mt-6 w-full">
              <Link to="/sign-in">
                Sign in
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}

        {stage === "code" && (
          <form onSubmit={verify} className="rounded-2xl border border-border/80 bg-card p-8 shadow-card">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-primary/12 text-primary">
              <MailCheck className="h-7 w-7" />
            </div>
            <h1 className="mt-5 text-center text-xl font-bold text-foreground">Check your email</h1>
            <p className="mt-2 text-center text-sm leading-6 text-muted-foreground">
              We sent a 6-digit code to{" "}
              <span className="font-semibold text-foreground">{maskedEmail}</span>. Enter it below to
              view the listing shared with you.
            </p>

            <div className="mt-6">
              <label htmlFor="guest-code" className="sr-only">
                Access code
              </label>
              <Input
                id="guest-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder="______"
                className="h-14 text-center font-mono text-2xl tracking-[0.5em]"
              />
            </div>

            <Button type="submit" disabled={verifying || code.length < 4} className="mt-5 w-full" size="lg">
              {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              {verifying ? "Verifying" : "View listing"}
            </Button>

            <div className="mt-4 text-center text-xs text-muted-foreground">
              {resendIn > 0 ? (
                <>You can request a new code in {resendIn}s</>
              ) : (
                <button
                  type="button"
                  onClick={() => void requestCode()}
                  className="font-semibold text-primary underline-offset-2 hover:underline"
                >
                  Send me a new code
                </button>
              )}
            </div>

            <p className="mt-5 border-t border-border/60 pt-4 text-center text-xs leading-5 text-muted-foreground">
              The code always goes to the address this listing was shared with, so forwarding the link
              won&rsquo;t give anyone else access.
            </p>
          </form>
        )}
      </div>
    </GuestShell>
  );
}
